// Composable for handling markdown formatting hotkeys in textareas
// Supports Telegram-compatible shortcuts:
// - Ctrl+B (bold), Ctrl+I (italic), Ctrl+U (underline)
// - Ctrl+Shift+X (strikethrough), Ctrl+Shift+. (quote)
// - Ctrl+Shift+M (monospace), Ctrl+Shift+P (spoiler)
// - Ctrl+K (link)

export function useMarkdownHotkeys(textareaRef) {
  // Helper to update textarea and trigger Vue's v-model binding
  const updateTextareaValue = (textarea, newValue, cursorPos) => {
    // Update the value
    textarea.value = newValue

    // Trigger input event to update v-model binding
    // Use InputEvent for better Vue 3 compatibility
    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
    })
    textarea.dispatchEvent(inputEvent)

    // Also trigger 'change' event as fallback
    const changeEvent = new Event('change', { bubbles: true })
    textarea.dispatchEvent(changeEvent)

    // Restore cursor position after a brief delay to ensure DOM is updated
    setTimeout(() => {
      if (cursorPos !== undefined) {
        textarea.setSelectionRange(cursorPos, cursorPos)
      }
      textarea.focus()
    }, 0)
  }

  const formatSelection = (before, after, placeholder = '') => {
    const textarea = textareaRef.value
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)
    const beforeText = textarea.value.substring(0, start)
    const afterText = textarea.value.substring(end)

    // If no selection, insert placeholder text with formatting
    const textToFormat = selectedText || placeholder

    // Apply formatting
    const formattedText = `${before}${textToFormat}${after}`

    // Calculate new cursor position
    let newCursorPos = start + before.length + textToFormat.length + after.length

    // If there was no selection, position cursor in the middle of the placeholder
    if (!selectedText && placeholder) {
      newCursorPos = start + before.length + placeholder.length
    }

    // Update textarea value and trigger v-model update
    const newValue = beforeText + formattedText + afterText
    updateTextareaValue(textarea, newValue, newCursorPos)
  }

  const handleKeyDown = event => {
    const isCtrl = event.ctrlKey || event.metaKey
    const isShift = event.shiftKey
    const key = event.key.toLowerCase()

    // Handle Ctrl+Shift combinations
    if (isCtrl && isShift) {
      const shiftShortcuts = {
        x: () => {
          event.preventDefault()
          formatSelection('~', '~', 'strikethrough text')
        },
        '.': () => {
          event.preventDefault()
          const textarea = textareaRef.value
          if (!textarea) return

          const start = textarea.selectionStart
          const end = textarea.selectionEnd
          const selectedText = textarea.value.substring(start, end)
          const beforeText = textarea.value.substring(0, start)
          const afterText = textarea.value.substring(end)

          let formattedText
          if (selectedText) {
            const lines = selectedText.split('\n')
            formattedText = lines.map(line => (line ? `> ${line}` : '')).join('\n')
          } else {
            formattedText = '> quotation text'
          }

          const newValue = beforeText + formattedText + afterText
          const newCursorPos = start + formattedText.length
          updateTextareaValue(textarea, newValue, newCursorPos)
        },
        m: () => {
          event.preventDefault()
          formatSelection('```\n', '\n```', 'code block')
        },
        p: () => {
          event.preventDefault()
          formatSelection('||', '||', 'spoiler text')
        },
      }

      if (shiftShortcuts[key]) {
        shiftShortcuts[key]()
        return
      }
    }

    // Handle Ctrl-only combinations
    if (isCtrl && !isShift) {
      const shortcuts = {
        b: () => {
          event.preventDefault()
          formatSelection('*', '*', 'bold text')
        },
        i: () => {
          event.preventDefault()
          formatSelection('_', '_', 'italic text')
        },
        u: () => {
          event.preventDefault()
          formatSelection('__', '__', 'underlined text')
        },
        k: () => {
          event.preventDefault()
          const textarea = textareaRef.value
          if (!textarea) return

          const start = textarea.selectionStart
          const end = textarea.selectionEnd
          const selectedText = textarea.value.substring(start, end)
          const beforeText = textarea.value.substring(0, start)
          const afterText = textarea.value.substring(end)

          let linkText
          let urlStart

          if (selectedText) {
            // Wrap selected text in link format
            linkText = `[${selectedText}](url)`
            urlStart = start + linkText.indexOf('url')
          } else {
            // Insert link placeholder
            linkText = '[link text](url)'
            urlStart = start + linkText.indexOf('url')
          }

          // Update textarea value and trigger v-model update
          const newValue = beforeText + linkText + afterText
          updateTextareaValue(textarea, newValue, urlStart)

          // Select 'url' part for easy editing (after the input event fires)
          setTimeout(() => {
            textarea.setSelectionRange(urlStart, urlStart + 3)
          }, 10)
        },
      }

      if (shortcuts[key]) {
        shortcuts[key]()
      }
    }
  }

  return {
    handleKeyDown,
  }
}
